import React, { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";

interface UserProgress {
  id: string;
  name: string;
  profileUrl: string;
  completed: number;
  skipped: number;
  total: number;
}

interface TeamTaskProgressProps {
  users: UserProgress[];
  calculateProgress: (user: UserProgress) => number;
}

export const TeamTaskProgress: React.FC<TeamTaskProgressProps> = ({
  users,
  calculateProgress,
}) => {
  const { t } = useTranslation();

  const OVERLAP_THRESHOLD = 0.4;
  const GROUP_COLLISION_THRESHOLD = 0.1;

  const progressBarRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [animationDelays, setAnimationDelays] = useState<number[]>([]);
  const [animationsComplete, setAnimationsComplete] = useState(false);
  const [teamViewPopoverOpen, setTeamViewPopoverOpen] = useState(false);
  const [teamViewPopoverUsers, setTeamViewPopoverUsers] = useState<
    UserProgress[]
  >([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [currentVisibleUsers, setCurrentVisibleUsers] = useState<
    {
      user: UserProgress;
      progress: number;
      groupedCount: number;
      groupedUsers: UserProgress[];
    }[]
  >([]);
  const [pendingVisibleUsers, setPendingVisibleUsers] = useState<
    typeof currentVisibleUsers
  >([]);
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set());
  const [animationPhase, setAnimationPhase] = useState<
    "scale-down" | "scale-up" | null
  >(null);

  const isInitialMountRef = useRef(true);
  const previousVisibleUsersRef = useRef<typeof currentVisibleUsers>([]);
  const currentVisibleUsersRef = useRef<typeof currentVisibleUsers>([]);

  const processOverlaps = useCallback(() => {
    if (users.length === 0) return [];

    const progressBarWidth = progressBarRef.current?.offsetWidth || 1000;
    const imageSize = imageRef.current?.offsetWidth || 24;

    const usersWithProgress = users.map((user) => ({
      user,
      progress: calculateProgress(user),
    }));

    usersWithProgress.sort((a, b) => b.progress - a.progress);

    const result: {
      user: UserProgress;
      progress: number;
      groupedCount: number;
      groupedUsers: UserProgress[];
    }[] = [];

    for (const current of usersWithProgress) {
      let isGrouped = false;

      for (const processed of result) {
        const processedPixelPos = (processed.progress / 100) * progressBarWidth;
        const currentPixelPos = (current.progress / 100) * progressBarWidth;

        const pixelDistance = Math.abs(processedPixelPos - currentPixelPos);
        const overlapPixels = imageSize - pixelDistance;

        if (overlapPixels > 0) {
          const visiblePortion = (imageSize - overlapPixels) / imageSize;
          if (visiblePortion < OVERLAP_THRESHOLD) {
            processed.groupedCount++;
            processed.groupedUsers.push(current.user);
            processed.progress = Math.max(processed.progress, current.progress);
            isGrouped = true;
            break;
          }
        }
      }

      if (!isGrouped) {
        result.push({
          user: current.user,
          progress: current.progress,
          groupedCount: 1,
          groupedUsers: [current.user],
        });
      }
    }

    const finalResult: typeof result = [];

    for (const current of result) {
      let shouldMerge = false;
      let mergeTarget: typeof current | null = null;

      for (const processed of finalResult) {
        if (processed.groupedCount > 1 || current.groupedCount > 1) {
          const processedPixelPos =
            (processed.progress / 100) * progressBarWidth;
          const currentPixelPos = (current.progress / 100) * progressBarWidth;

          const pixelDistance = Math.abs(processedPixelPos - currentPixelPos);
          const overlapPixels = imageSize - pixelDistance;

          if (overlapPixels > imageSize * GROUP_COLLISION_THRESHOLD) {
            shouldMerge = true;
            mergeTarget = processed;
            break;
          }
        }
      }

      if (shouldMerge && mergeTarget) {
        mergeTarget.groupedCount += current.groupedCount;
        mergeTarget.groupedUsers.push(...current.groupedUsers);
        mergeTarget.progress = Math.max(mergeTarget.progress, current.progress);
      } else {
        finalResult.push(current);
      }
    }

    return finalResult;
  }, [users, calculateProgress]);

  const usersStatsKey = users
    .map((u) => `${u.id}-${u.completed}-${u.skipped}-${u.total}`)
    .join(",");

  useEffect(() => {
    const newVisibleUsers = processOverlaps();

    if (isInitialMountRef.current) {
      setAnimationsComplete(false);
      const delays = users.map(() => Math.random() * 500);
      setAnimationDelays(delays);
      setCurrentVisibleUsers(newVisibleUsers);
      currentVisibleUsersRef.current = newVisibleUsers;
      previousVisibleUsersRef.current = newVisibleUsers;

      const maxDelay = Math.max(...delays, 0);
      setTimeout(() => {
        setAnimationsComplete(true);
        isInitialMountRef.current = false;
      }, maxDelay + 200);
    } else {
      const changedIds = new Set<string>();

      currentVisibleUsersRef.current.forEach((currentItem) => {
        const newItem = newVisibleUsers.find(
          (item) => item.user.id === currentItem.user.id,
        );

        if (newItem) {
          const progressChanged = currentItem.progress !== newItem.progress;
          const groupCountChanged =
            currentItem.groupedCount !== newItem.groupedCount;

          if (progressChanged || groupCountChanged) {
            changedIds.add(currentItem.user.id);
          }
        } else {
          changedIds.add(currentItem.user.id);
        }
      });

      newVisibleUsers.forEach((newItem) => {
        const existsInCurrent = currentVisibleUsersRef.current.find(
          (item) => item.user.id === newItem.user.id,
        );
        if (!existsInCurrent) {
          changedIds.add(newItem.user.id);
        }
      });

      if (changedIds.size > 0) {
        setPendingVisibleUsers(newVisibleUsers);
        setAnimatingIds(changedIds);
        setAnimationPhase("scale-down");
        previousVisibleUsersRef.current = newVisibleUsers;
      } else {
        setCurrentVisibleUsers(newVisibleUsers);
        currentVisibleUsersRef.current = newVisibleUsers;
        previousVisibleUsersRef.current = newVisibleUsers;
      }
    }
  }, [usersStatsKey, processOverlaps, users]);

  const handleAnimationEnd = (userId: string) => {
    if (animationPhase === "scale-down") {
      setCurrentVisibleUsers(pendingVisibleUsers);
      currentVisibleUsersRef.current = pendingVisibleUsers;
      setAnimationPhase("scale-up");
    } else if (animationPhase === "scale-up") {
      setAnimatingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);

        if (next.size === 0) {
          setAnimationPhase(null);
          setPendingVisibleUsers([]);
        }

        return next;
      });
    }
  };

  const handleTeamAvatarHover = (item: {
    user: UserProgress;
    progress: number;
    groupedCount: number;
    groupedUsers: UserProgress[];
  }) => {
    setTeamViewPopoverUsers(item.groupedUsers);
    setTeamViewPopoverOpen(true);
  };

  const handleAvatarMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget?.closest("[data-radix-popper-content-wrapper]")) {
      setHoveredIndex(null);
      setTeamViewPopoverOpen(false);
    }
  };

  const handleShowAllClick = () => {
    setHoveredIndex(null);
    setTeamViewPopoverOpen(false);
    setDialogOpen(true);
  };

  const renderUserCard = (user: UserProgress, userIndex: number) => {
    const userProgress = calculateProgress(user);
    const skipRate =
      user.total > 0 ? Math.round((user.skipped / user.total) * 100) : 0;

    return (
      <div key={userIndex} className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1">
          <img
            className="h-4 w-4 rounded-full border border-background"
            src={user.profileUrl}
            alt={user.name}
          />
          <div className="text-xs leading-4 font-medium whitespace-nowrap text-primary">
            {user.name}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs leading-3 whitespace-nowrap text-muted-foreground">
              {t("strategyBoard.progress.team.popover.completed")}
            </div>
            <div className="text-xs leading-3 font-medium whitespace-nowrap text-primary">
              {userProgress}%
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs leading-3 whitespace-nowrap text-muted-foreground">
              {t("strategyBoard.progress.team.popover.skipRate")}
            </div>
            <div className="text-xs leading-3 font-medium whitespace-nowrap text-primary">
              {skipRate}%
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTeamPopoverContent = () => (
    <>
      <div
        className={`grid gap-6 ${teamViewPopoverUsers.length > 2 ? "grid-cols-2" : "grid-cols-1"}`}
      >
        {teamViewPopoverUsers.slice(0, 4).map(renderUserCard)}
      </div>
      {teamViewPopoverUsers.length >= 5 && (
        <button
          className="mt-4 w-full rounded-md bg-secondary px-4 py-2 text-sm leading-5 font-medium whitespace-nowrap text-secondary-foreground outline-none"
          onClick={handleShowAllClick}
        >
          {t("strategyBoard.progress.team.popover.showAll")}
        </button>
      )}
    </>
  );

  const renderDialogContent = () => (
    <div className="team-progress-dialog-scroll grid max-h-[210px] grid-cols-2 gap-6 pr-4">
      {teamViewPopoverUsers.map(renderUserCard)}
    </div>
  );

  const getAnimationClass = (isAnimating: boolean) => {
    if (!isAnimating) return "";
    if (animationPhase === "scale-down") return "animate-scale-down";
    if (animationPhase === "scale-up") return "animate-scale-up";
    return "";
  };

  return (
    <>
      <style>
        {`
          @keyframes pop-in {
            0% {
              transform: translateY(-50%) scale(0.1);
              opacity: 0;
            }
            100% {
              transform: translateY(-50%) scale(1);
              opacity: 1;
            }
          }
          
          .animate-pop-in {
            animation: pop-in 200ms ease-out forwards;
            opacity: 0;
          }
          
          @keyframes scale-down {
            from {
              transform: translateY(-50%) scale(1);
            }
            to {
              transform: translateY(-50%) scale(0.1);
            }
          }

          @keyframes scale-up {
            from {
              transform: translateY(-50%) scale(0.1);
            }
            to {
              transform: translateY(-50%) scale(1);
            }
          }

          .animate-scale-down {
            animation: scale-down 200ms ease-in-out forwards;
          }

          .animate-scale-up {
            animation: scale-up 200ms ease-out forwards;
          }

          .team-progress-dialog-scroll {
            overflow-y: scroll;
            scrollbar-gutter: stable;
          }

          .team-progress-dialog-scroll::-webkit-scrollbar {
            width: 8px;
          }

          .team-progress-dialog-scroll::-webkit-scrollbar-track {
            background: transparent;
          }

          .team-progress-dialog-scroll::-webkit-scrollbar-thumb {
            background: var(--color-border);
            border-radius: 4px;
          }

          .team-progress-dialog-scroll::-webkit-scrollbar-thumb:hover {
            background: var(--color-border-dark);
          }
        `}
      </style>

      <div ref={progressBarRef} className="relative mr-3 mb-6 h-2">
        <div className="absolute inset-0 overflow-hidden rounded-xl bg-blue-500">
          <div
            className="h-full w-[120%] -translate-x-[10%]"
            style={{
              background: `repeating-linear-gradient(
                -80deg,
                color-mix(in srgb, var(--color-blue-500), var(--color-primary-foreground) 20%) 0px,
                color-mix(in srgb, var(--color-blue-500), var(--color-primary-foreground) 20%) 8px,
                transparent 8px,
                transparent 16px
              )`,
            }}
          />
        </div>

        {currentVisibleUsers.map((item, index) => {
          const zIndex = currentVisibleUsers.length - index;
          const isFirst = index === 0;
          const isHovered = hoveredIndex === index;
          const animationDelay = animationDelays[index] || 0;
          const isAnimating = animatingIds.has(item.user.id);
          const shouldShowIntro =
            isInitialMountRef.current && !animationsComplete;

          if (item.groupedCount === 1) {
            return (
              <Popover
                key={item.user.id}
                open={teamViewPopoverOpen && hoveredIndex === index}
                onOpenChange={setTeamViewPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <img
                    ref={
                      isFirst
                        ? (imageRef as React.RefObject<HTMLImageElement>)
                        : undefined
                    }
                    className={`absolute top-1/2 h-6 w-6 cursor-pointer rounded-full border border-background ${
                      shouldShowIntro ? "animate-pop-in" : ""
                    } ${getAnimationClass(isAnimating)}`}
                    src={item.user.profileUrl}
                    alt={item.user.name}
                    style={{
                      left: `calc(${item.progress}% - 12px)`,
                      zIndex: isHovered ? 9999 : zIndex,
                      transform: isHovered
                        ? "translateY(-50%) scale(1.667)"
                        : "translateY(-50%) scale(1)",
                      animationDelay: shouldShowIntro
                        ? `${animationDelay}ms`
                        : "0ms",
                      transition:
                        animationsComplete && !isAnimating
                          ? "transform 200ms ease-out, z-index 0s"
                          : "none",
                    }}
                    onAnimationEnd={() => handleAnimationEnd(item.user.id)}
                    onMouseEnter={() => {
                      setHoveredIndex(index);
                      handleTeamAvatarHover(item);
                    }}
                    onMouseLeave={handleAvatarMouseLeave}
                  />
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-6"
                  align="center"
                  side="bottom"
                  sideOffset={0}
                  onMouseEnter={() => setTeamViewPopoverOpen(true)}
                  onMouseLeave={() => {
                    setTeamViewPopoverOpen(false);
                    setHoveredIndex(null);
                  }}
                >
                  {renderTeamPopoverContent()}
                </PopoverContent>
              </Popover>
            );
          } else {
            const badgeCount = item.groupedCount - 1;
            return (
              <Popover
                key={item.user.id}
                open={teamViewPopoverOpen && hoveredIndex === index}
                onOpenChange={setTeamViewPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <div
                    className={`absolute top-1/2 cursor-pointer ${
                      shouldShowIntro ? "animate-pop-in" : ""
                    } ${getAnimationClass(isAnimating)}`}
                    style={{
                      left: `calc(${item.progress}% - 12px)`,
                      zIndex: isHovered ? 9999 : zIndex,
                      transform: isHovered
                        ? "translateY(-50%) scale(1.667)"
                        : "translateY(-50%) scale(1)",
                      animationDelay: shouldShowIntro
                        ? `${animationDelay}ms`
                        : "0ms",
                      transition:
                        animationsComplete && !isAnimating
                          ? "transform 200ms ease-out, z-index 0s"
                          : "none",
                    }}
                    onAnimationEnd={() => handleAnimationEnd(item.user.id)}
                  >
                    <img
                      ref={
                        isFirst
                          ? (imageRef as React.RefObject<HTMLImageElement>)
                          : undefined
                      }
                      className="h-6 w-6 rounded-full border border-background"
                      src={item.user.profileUrl}
                      alt={item.user.name}
                      onMouseEnter={() => {
                        setHoveredIndex(index);
                        handleTeamAvatarHover(item);
                      }}
                      onMouseLeave={handleAvatarMouseLeave}
                    />
                    <div className="pointer-events-none absolute -top-2 -right-2 flex h-[20px] min-w-[20px] items-center justify-center rounded-full border border-background bg-foreground px-0.5">
                      <span className="text-[8px] leading-none font-semibold text-background">
                        +{badgeCount}
                      </span>
                    </div>
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-6"
                  align="center"
                  side="bottom"
                  sideOffset={0}
                  onMouseEnter={() => setTeamViewPopoverOpen(true)}
                  onMouseLeave={() => {
                    setTeamViewPopoverOpen(false);
                    setHoveredIndex(null);
                  }}
                >
                  {renderTeamPopoverContent()}
                </PopoverContent>
              </Popover>
            );
          }
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {t("strategyBoard.progress.team.popoverDialog.title")}
            </DialogTitle>
            <div className="text-sm text-muted-foreground">
              {t("strategyBoard.progress.team.popoverDialog.members", {
                count: teamViewPopoverUsers.length,
              })}
            </div>
          </DialogHeader>
          {renderDialogContent()}
          <div className="flex justify-end pt-4">
            <button
              className="rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground outline-none"
              style={{ border: "none" }}
              onClick={() => setDialogOpen(false)}
            >
              {t("global.close")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
